// Regras da tela Ferramentas → Consulta de dados (sem I/O). A tela tem um campo só: a pessoa digita
// um CPF, um CNPJ ou um nome, e o mesmo campo filtra a lista do que já foi consultado.

import { validarCNPJ, validarCPF } from './utils.ts';

export type TipoDocumento = 'cpf' | 'cnpj';

/** Uma linha de `consultas_cpf_cnpj`. */
export interface ConsultaSalva {
  documento: string;
  tipo: TipoDocumento;
  nome: string;
  /** AAAA-MM-DD; null em CNPJ ou quando a API não informa. */
  nascimento: string | null;
  consultado_por_nome: string | null;
  consultado_em: string;
}

/** Resposta da edge function consultar-cpf-cnpj. */
export interface ResultadoConsulta extends ConsultaSalva {
  /** 'salvo' = já estava no banco, nenhum crédito usado. */
  origem: 'salvo' | 'api';
  saldo: number | null;
}

/** De onde a tela lê e consulta. No app é o Supabase; na pré-visualização, dados fictícios. */
export interface FonteConsultas {
  listar(filtro: { digitos?: string; nome?: string; limite: number }): Promise<{ itens: ConsultaSalva[]; total: number }>;
  consultar(documento: string, forcar: boolean): Promise<ResultadoConsulta>;
  ultimoSaldo(): Promise<{ saldo: number; em: string } | null>;
}

/** O que foi digitado na busca. Discriminante de texto: o tsc do projeto não é estrito. */
export type EntradaBusca =
  | { tipo: 'vazia' }
  | { tipo: 'documento'; documento: string; tipoDocumento: TipoDocumento; valido: boolean }
  | { tipo: 'parcial'; digitos: string }
  | { tipo: 'nome'; texto: string };

/** Nome com menos letras que isso não filtra (evita listar meio banco ao digitar "a"). */
export const MINIMO_LETRAS_NOME = 2;

export function classificarBusca(valor: string): EntradaBusca {
  const texto = valor.trim();
  if (!texto) return { tipo: 'vazia' };

  // Só números e pontuação de documento: é CPF/CNPJ (inteiro ou pela metade).
  if (/^[\d.\-/\s]+$/.test(texto)) {
    const digitos = texto.replace(/\D/g, '');
    if (!digitos) return { tipo: 'vazia' };
    if (digitos.length === 11) return { tipo: 'documento', documento: digitos, tipoDocumento: 'cpf', valido: validarCPF(digitos) };
    if (digitos.length === 14) return { tipo: 'documento', documento: digitos, tipoDocumento: 'cnpj', valido: validarCNPJ(digitos) };
    return { tipo: 'parcial', digitos };
  }
  return { tipo: 'nome', texto };
}

/** 000.000.000-00 ou 00.000.000/0000-00. Outro tamanho volta só com os dígitos. */
export function formatarDocumento(documento: string): string {
  const d = documento.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return d;
}

/** "1985-03-07" → "07/03/1985". Vazio ou fora do formato → ''. */
export function formatarNascimento(iso: string | null | undefined): string {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** Escapa % e _ para o `ilike` do PostgREST tratar o texto ao pé da letra. */
export function padraoContem(texto: string): string {
  return `%${texto.replace(/[\\%_]/g, c => `\\${c}`)}%`;
}

/** "28/10/2026 às 14:32" no horário de Brasília. */
export function formatarMomento(iso: string | null | undefined): string {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).formatToParts(data);
  const p = (t: string) => partes.find(x => x.type === t)?.value ?? '';
  return `${p('day')}/${p('month')}/${p('year')} às ${p('hour')}:${p('minute')}`;
}
